<template>
  <div class="shop-dine-qrcode-page">
    <Card>
      <p slot="title">堂食桌码管理</p>
      <Alert show-icon>
        现为<strong>普通二维码</strong>（一个码两种扫法）：小程序内「扫码绑桌/换桌」可直接识别；微信「扫一扫」会打开跳转页再进入小程序。
      </Alert>

      <Form :label-width="120" class="m-t-16">
        <FormItem label="生成方式">
          <RadioGroup v-model="genMode">
            <Radio label="range">桌号区间</Radio>
            <Radio label="list">指定桌号</Radio>
          </RadioGroup>
        </FormItem>
        <FormItem v-if="genMode === 'range'" label="桌号区间">
          <InputNumber v-model="form.table_from" :min="1" :max="9999" style="width: 120px" />
          <span class="range-sep">至</span>
          <InputNumber v-model="form.table_to" :min="1" :max="9999" style="width: 120px" />
          <span class="hint m-l-10">单次最多 500 张</span>
        </FormItem>
        <FormItem v-else label="桌号列表">
          <Input
            v-model="form.tables_text"
            type="textarea"
            :rows="3"
            placeholder="多个桌号用逗号、空格或换行分隔，如：1,2,8,A12"
            style="max-width: 480px"
          />
        </FormItem>
        <FormItem label="小程序版本">
          <Select v-model="form.env_version" clearable style="width: 200px" placeholder="默认 release">
            <Option value="release">正式版 release</Option>
            <Option value="trial">体验版 trial</Option>
            <Option value="develop">开发版 develop</Option>
          </Select>
          <span class="hint m-l-10">写入跳转页，微信扫一扫进对应版本</span>
        </FormItem>
        <FormItem>
          <Button type="primary" :loading="generating" @click="handleBatchGenerate">
            批量生成桌码
          </Button>
          <Button class="m-l-10" :loading="listLoading" @click="loadSavedList">刷新已生成</Button>
        </FormItem>
      </Form>
    </Card>

    <Card v-if="lastBatch" class="m-t-16">
      <p slot="title">
        本次生成结果
        <Tag color="success" class="m-l-8">成功 {{ lastBatch.success }}</Tag>
        <Tag v-if="lastBatch.failed_count" color="error" class="m-l-8">失败 {{ lastBatch.failed_count }}</Tag>
      </p>
      <Table
        v-if="lastBatch.list && lastBatch.list.length"
        :columns="resultColumns"
        :data="lastBatch.list"
        border
        stripe
        size="small"
      />
      <div v-if="lastBatch.failed && lastBatch.failed.length" class="m-t-16">
        <div class="failed-title">失败明细</div>
        <Table :columns="failedColumns" :data="lastBatch.failed" border size="small" />
      </div>
    </Card>

    <Card class="m-t-16">
      <p slot="title">已保存桌码（{{ savedList.length }}）</p>
      <div v-if="!savedList.length && !listLoading" class="empty-tip">暂无文件，请先生成桌码</div>
      <div v-else class="qrcode-grid">
        <div v-for="item in savedList" :key="item.filename" class="qrcode-card">
          <div class="qrcode-card__img-wrap">
            <img :src="imageFullUrl(item.image_url)" :alt="item.scene" class="qrcode-card__img" />
          </div>
          <div class="qrcode-card__meta">
            <div class="qrcode-card__scene">{{ item.scene }}</div>
            <div class="qrcode-card__actions">
              <a :href="imageFullUrl(item.image_url)" target="_blank" rel="noopener">预览</a>
              <a class="m-l-8" :href="imageFullUrl(item.image_url)" :download="item.filename">下载</a>
              <a
                class="m-l-8 qrcode-card__del"
                href="javascript:;"
                :class="{ 'is-disabled': deletingFilename === item.filename }"
                @click.prevent="handleDelete(item)"
              >删除</a>
            </div>
          </div>
        </div>
      </div>
    </Card>
  </div>
</template>

<script>
import { shopDineBatchQrcodes, shopDineQrcodeList, shopDineQrcodeDelete } from '@/api/shopDine'

export default {
  name: 'ShopDineQrcodeManage',
  data() {
    return {
      genMode: 'range',
      form: {
        table_from: 1,
        table_to: 10,
        tables_text: '',
        env_version: 'trial',
      },
      generating: false,
      listLoading: false,
      deletingFilename: '',
      lastBatch: null,
      savedList: [],
      failedColumns: [
        { title: '桌号', key: 'table_no', width: 100 },
        { title: 'scene', key: 'scene', minWidth: 120 },
        { title: '原因', key: 'msg', minWidth: 200 },
      ],
    }
  },
  computed: {
    resultColumns() {
      const h = this.$createElement
      return [
        { title: '桌号', key: 'table_no', width: 90 },
        { title: 'scene', key: 'scene', width: 120 },
        {
          title: '二维码',
          key: 'image_url',
          width: 120,
          align: 'center',
          render: (h, { row }) => {
            if (!row.image_url) return h('span', '—')
            return h('img', {
              attrs: {
                src: this.imageFullUrl(row.image_url),
                alt: row.scene,
              },
              style: { width: '80px', height: '80px', objectFit: 'contain' },
            })
          },
        },
        {
          title: '操作',
          key: 'action',
          width: 140,
          render: (h, { row }) => {
            if (!row.image_url) return h('span', '—')
            const url = this.imageFullUrl(row.image_url)
            return h('div', [
              h(
                'a',
                {
                  attrs: { href: url, target: '_blank', rel: 'noopener' },
                },
                '预览'
              ),
              h(
                'a',
                {
                  attrs: { href: url, download: row.filename || `${row.scene}.png` },
                  style: { marginLeft: '12px' },
                },
                '下载'
              ),
            ])
          },
        },
      ]
    },
  },
  created() {
    this.loadSavedList()
  },
  methods: {
    imageFullUrl(path) {
      if (!path) return ''
      if (/^https?:\/\//i.test(path)) return path
      const apiBase = (process.env.VUE_APP_BASEURL || '').replace(/\/$/, '')
      const origin = apiBase.replace(/\/api$/i, '')
      const p = path.startsWith('/') ? path : `/${path}`
      return `${origin}${p}`
    },
    buildBatchPayload() {
      const payload = {
        save_static: true,
        return_base64: false,
      }
      if (this.form.env_version) {
        payload.env_version = this.form.env_version
      }
      if (this.genMode === 'range') {
        payload.table_from = this.form.table_from
        payload.table_to = this.form.table_to
      } else {
        const text = (this.form.tables_text || '').trim()
        if (!text) {
          this.$Message.warning('请填写桌号列表')
          return null
        }
        payload.tables = text
          .split(/[,，\s\n]+/)
          .map((s) => s.trim())
          .filter(Boolean)
        if (!payload.tables.length) {
          this.$Message.warning('桌号列表无效')
          return null
        }
      }
      return payload
    },
    handleBatchGenerate() {
      const payload = this.buildBatchPayload()
      if (!payload) return
      if (this.genMode === 'range') {
        const from = Number(payload.table_from)
        const to = Number(payload.table_to)
        if (!Number.isFinite(from) || !Number.isFinite(to)) {
          this.$Message.warning('请填写有效桌号区间')
          return
        }
        if (to - from + 1 > 500) {
          this.$Message.warning('区间超过 500 张，请缩小范围')
          return
        }
      }
      this.generating = true
      shopDineBatchQrcodes(payload)
        .then((res) => {
          this.generating = false
          const body = res.data || {}
          if (body.code === 200 && body.data) {
            this.lastBatch = body.data
            this.$Message.success(body.msg || '生成完成')
            this.loadSavedList()
          } else {
            this.$Message.error(body.msg || '生成失败')
          }
        })
        .catch((err) => {
          this.generating = false
          this.$Message.error((err && err.message) || '请求失败')
        })
    },
    loadSavedList() {
      this.listLoading = true
      shopDineQrcodeList()
        .then((res) => {
          this.listLoading = false
          const body = res.data || {}
          if (body.code === 200 && body.data) {
            this.savedList = body.data.list || []
          } else {
            this.savedList = []
            if (body.msg) this.$Message.error(body.msg)
          }
        })
        .catch(() => {
          this.listLoading = false
          this.savedList = []
        })
    },
    handleDelete(item) {
      if (!item || !item.filename || this.deletingFilename) return
      this.$Modal.confirm({
        title: '删除桌码',
        content: `确认删除桌码「${item.scene || item.filename}」？删除后列表中将不再显示该图片。`,
        onOk: () => {
          this.deletingFilename = item.filename
          return shopDineQrcodeDelete({ filename: item.filename })
            .then((res) => {
              this.deletingFilename = ''
              const body = (res && res.data) || {}
              if (body.code === 200) {
                this.$Message.success(body.msg || '删除成功')
                this.savedList = this.savedList.filter((x) => x.filename !== item.filename)
              } else {
                const tip =
                  (typeof body === 'object' && body.msg) ||
                  (res && res.status === 404
                    ? '删除接口不存在，请先部署最新后端'
                    : null) ||
                  '删除失败'
                this.$Message.error(tip)
              }
            })
            .catch((err) => {
              this.deletingFilename = ''
              const tip =
                (typeof err === 'string' && err) ||
                (err && err.message) ||
                '删除失败（请确认后端已部署）'
              this.$Message.error(tip)
            })
        },
      })
    },
  },
}
</script>

<style scoped>
.shop-dine-qrcode-page {
  padding: 0;
}
.m-t-16 {
  margin-top: 16px;
}
.m-l-8 {
  margin-left: 8px;
}
.m-l-10 {
  margin-left: 10px;
}
.range-sep {
  margin: 0 12px;
  color: #666;
}
.hint {
  font-size: 12px;
  color: #999;
}
.failed-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #ed4014;
}
.empty-tip {
  color: #999;
  padding: 24px 0;
  text-align: center;
}
.qrcode-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}
.qrcode-card {
  border: 1px solid #e8eaec;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
}
.qrcode-card__img-wrap {
  padding: 12px;
  text-align: center;
  background: #fff;
}
.qrcode-card__img {
  width: 120px;
  height: 120px;
  object-fit: contain;
}
.qrcode-card__meta {
  padding: 8px 12px 12px;
  border-top: 1px solid #e8eaec;
}
.qrcode-card__scene {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  word-break: break-all;
}
.qrcode-card__actions {
  margin-top: 8px;
  font-size: 12px;
}
.qrcode-card__del {
  color: #ed4014;
}
.qrcode-card__del.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}
code {
  padding: 2px 6px;
  background: #f4f4f5;
  border-radius: 4px;
  font-size: 12px;
}
</style>
